import axios from "axios";
import { v4 as uuidv4 } from "uuid";

const STORAGE_KEY = "assistant_session_id";

export interface ChatMessage {
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export class AssistantService {
  private sessionId: string;

  constructor() {
    // Try to get existing session ID from storage or create a new one
    const storedSessionId = localStorage.getItem(STORAGE_KEY);
    if (storedSessionId) {
      this.sessionId = storedSessionId;
    } else {
      this.sessionId = uuidv4();
      localStorage.setItem(STORAGE_KEY, this.sessionId);
    }
  }

  /**
   * Create a new session
   */
  createNewSession(): void {
    this.sessionId = uuidv4();
    localStorage.setItem(STORAGE_KEY, this.sessionId);
  }

  /**
   * Send a message to the AI assistant with streaming response
   */
  async sendMessage(
    prompt: string,
    onChunk: (chunk: string) => void,
    token?: string,
  ): Promise<void> {
    try {
      // Keep track of the received data
      let buffer = "";
      let hasReceivedData = false;

      // Set up timeout to handle case where server never responds
      const timeoutId = setTimeout(() => {
        if (!hasReceivedData) {
          throw new Error(
            "Request timed out. The assistant service might be unavailable.",
          );
        }
      }, 30000); // 30 second client-side timeout

      // Use the provided token if present, otherwise check localStorage
      const customAuthToken =
        token || localStorage.getItem("assistant_auth_token");
      const headers: Record<string, string> = {};
      if (customAuthToken) {
        headers.Authorization = customAuthToken;
      }

      // Using the application's built-in authentication through cookies/headers
      await axios({
        method: "post",
        url: "/api/assistant/chat",
        headers,
        data: {
          session_id: this.sessionId,
          prompt,
        },
        responseType: "stream",
        timeout: 60000, // 60 second request timeout
        onDownloadProgress: (progressEvent) => {
          const rawChunk = progressEvent.event.target.response;

          if (rawChunk && rawChunk !== buffer) {
            // Mark that data has been received so we can clear timeout
            if (!hasReceivedData) {
              hasReceivedData = true;
              clearTimeout(timeoutId);
            }

            // Get only the new part of the response
            const newContent = rawChunk.substring(buffer.length);
            if (newContent) {
              buffer = rawChunk;
              onChunk(newContent);
            }
          }
        },
      });
    } catch (error: any) {
      console.error("Error sending message to assistant:", error);

      // Format more user-friendly error messages
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        throw new Error(
          "Request timed out. The assistant service might be experiencing high load or be temporarily unavailable.",
        );
      } else if (error.response?.status === 504) {
        throw new Error(
          "Gateway timeout. The assistant service is taking too long to respond.",
        );
      } else if (error.response?.status === 401) {
        throw new Error(
          "Authentication failed. Please log in again or refresh your session.",
        );
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to access this resource.");
      } else {
        throw error;
      }
    }
  }
}

// Create a singleton instance
const assistantService = new AssistantService();
export default assistantService;
